import type { DebugFlowsContainer } from '@elara/client-core/flows';
import type { ChatDebugState } from '@elara/client-core/states/ChatDebugState';
import type { ChatsState } from '@elara/client-core/states/ChatsState';
import type { AgentDebugInfo, DebugSkillInfo, WorkingMemoryDebugInfo } from '@elara/domain/debug-flows';
import type { DebugToolDefinition } from '@elara/domain/models/schemas/DebugToolDefinitionSchema';

import { createState } from '@elara/client-core/lib/signals-utils';

export type { AgentDebugInfo, WorkingMemoryDebugInfo } from '@elara/domain/debug-flows';

export function createDebugInspectorState(
  debugFlows: DebugFlowsContainer,
  chatsState: ChatsState,
  chatDebugState: ChatDebugState,
) {
  const { getTools, getChatState, getAgentState, getWorkingMemory, getSkills } = debugFlows;
  return createState({
    tools: [] as DebugToolDefinition[],
    loading: true,
    expandedTools: new Set<string>(),
    selectedTab: 'live',

    // Agent state
    agents: [] as AgentDebugInfo[],
    agentsLoading: false,
    expandedAgents: new Set<string>(),

    // Skills registry
    skills: [] as DebugSkillInfo[],
    skillsLoading: false,
    skillsError: null as string | null,
    expandedSkills: new Set<string>(),
    uncoveredStates: [] as string[],

    // Working memory state
    workingMemory: null as WorkingMemoryDebugInfo | null,
    workingMemoryLoading: false,

    context: {} as Record<string, unknown>,

    get primaryAgent(): AgentDebugInfo | undefined {
      return this.agents.find((a) => a.id === chatDebugState.agentId);
    },
    async refresh() {
      await Promise.all(
        [
          this.fetchTools(),
          this.fetchAgentState(),
          this.fetchSkills(),
          chatsState.current?.id ? this.fetchChatState() : undefined,
          chatsState.current?.id ? this.fetchWorkingMemory() : undefined,
        ].filter((x) => !!x),
      );
    },
    async fetchTools(): Promise<void> {
      console.debug('[DebugInspector] Fetching tools');
      try {
        this.loading = true;
        const result = await getTools({});
        console.debug('[DebugInspector] Fetched tools:', result.data);
        const data = result.data;
        try {
          this.context = data.context;
        } catch (error) {
          console.error('[DebugInspector] Failed to parse tool context:', error);
        }
        this.setTools((data.tools || []) as DebugToolDefinition[]);
      } catch (error) {
        console.error('[DebugInspector] Failed to fetch tools:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchChatState(): Promise<void> {
      const chatId = chatsState.current?.id;
      if (!chatId) return;
      console.debug('[DebugInspector] Fetching chat state for chatId:', chatId);
      try {
        const result = await getChatState({ chatId });
        console.debug('[DebugInspector] Fetched chat state:', result.data);
        const data = result.data;
        chatDebugState.update({
          messages: data.messages,
          messageCount: data.stats.totalMessages,
          lastActivity: data.lastActivity instanceof Date ? data.lastActivity : new Date(data.lastActivity),
          agentId: data.agentId,
        });
      } catch (error) {
        console.error('[DebugInspector] Failed to fetch chat state:', error);
      }
    },
    toggleToolExpansion(toolName: string): void {
      const newSet = new Set(this.expandedTools);
      if (newSet.has(toolName)) {
        newSet.delete(toolName);
      } else {
        newSet.add(toolName);
      }
      this.expandedTools = newSet;
    },
    setTools(tools: DebugToolDefinition[]): void {
      this.tools = dedupeToolsByName(tools);
      this.loading = false;
    },
    setSelectedTab(tab: string): void {
      this.selectedTab = tab;
    },
    async fetchAgentState(): Promise<void> {
      console.debug('[DebugInspector] Fetching agent state');
      try {
        this.agentsLoading = true;
        const result = await getAgentState(undefined);
        console.debug('[DebugInspector] Fetched agent state:', result.data);
        this.agents = result.data.agents ?? [];
      } catch (error) {
        console.error('[DebugInspector] Failed to fetch agent state:', error);
        this.agents = [];
      } finally {
        this.agentsLoading = false;
      }
    },
    toggleAgentExpansion(agentId: string): void {
      const newSet = new Set(this.expandedAgents);
      if (newSet.has(agentId)) {
        newSet.delete(agentId);
      } else {
        newSet.add(agentId);
      }
      this.expandedAgents = newSet;
    },
    async fetchSkills(): Promise<void> {
      try {
        this.skillsLoading = true;
        this.skillsError = null;
        const result = await getSkills({});
        this.skills = result.data.skills ?? [];
        this.uncoveredStates = result.data.uncoveredStates ?? [];
      } catch (error) {
        console.error('[DebugInspector] Failed to fetch skills:', error);
        this.skills = [];
        this.skillsError = error instanceof Error ? error.message : 'Failed to fetch skills';
      } finally {
        this.skillsLoading = false;
      }
    },
    toggleSkillExpansion(skillName: string): void {
      const newSet = new Set(this.expandedSkills);
      if (newSet.has(skillName)) {
        newSet.delete(skillName);
      } else {
        newSet.add(skillName);
      }
      this.expandedSkills = newSet;
    },
    async fetchWorkingMemory(): Promise<void> {
      const chatId = chatsState.current?.id;
      if (!chatId) return;
      console.debug('[DebugInspector] Fetching working memory for chatId:', chatId);
      try {
        this.workingMemoryLoading = true;
        const result = await getWorkingMemory({ chatId });
        console.debug('[DebugInspector] Fetched working memory:', result.data);
        this.workingMemory = result.data ?? null;
      } catch (error) {
        console.error('[DebugInspector] Failed to fetch working memory:', error);
        this.workingMemory = null;
      } finally {
        this.workingMemoryLoading = false;
      }
    },
  });
}

export type DebugInspectorState = ReturnType<typeof createDebugInspectorState>;

function dedupeToolsByName(tools: DebugToolDefinition[]): DebugToolDefinition[] {
  const seen = new Set<string>();
  return tools.filter((tool) => {
    const name = tool.name.trim().toLowerCase();
    if (!name) return false;
    if (seen.has(name)) return false;
    seen.add(name);
    return true;
  });
}
